"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { useAudioPlayerContext } from "@/contexts/audio-player-context";
import { useAudioUrl } from "@/hooks/useAudioUrl";
import { useAudioPeaks } from "@/hooks/useAudioPeaks";
import type { Sound } from "@/lib/supabase";
import { categoryBrowseHref } from "@/lib/category-data";
import { cleanSoundTitle, cn } from "@/lib/utils";
import { TESLA_REQUIREMENTS } from "@/lib/audio-utils";
import { soundUrl } from "@/lib/slugify";
import { audioPreloader } from "@/lib/audio-preloader";
import { useIsRecent } from "@/lib/hydration-utils";
import { useFavoriteSounds } from "@/hooks/useFavoriteSounds";
import {
  trackSavedSoundSaveClick,
  trackSavedSoundUnsaveClick,
  trackSavedSoundsPlayClick,
  trackAudioPlay,
  trackSoundDetailClick,
  trackPreviewPlay,
} from "@/lib/tracking";

// Extracted sound-card units
import { useEngagementTracking } from "@/components/ui/sound-card/use-engagement-tracking";
import { useCardDownload } from "@/components/ui/sound-card/use-card-download";
import { WaveformBand } from "@/components/ui/sound-card/waveform-band";
import { CardActions } from "@/components/ui/sound-card/card-actions";
import { SoundCardSkeleton } from "@/components/ui/sound-card-skeleton";

// ── Utilities ──────────────────────────────────────────────────────────────

/** Generate 48 pleasing peaks from a sound id (deterministic fallback). */
function generatePeaks(id: string, count = 48): number[] {
  const seed = id.split("").reduce((acc, ch, idx) => acc + ch.charCodeAt(0) * (idx + 1), 0);
  const peaks: number[] = [];
  for (let i = 0; i < count; i++) {
    const pos = i / count;
    const envelope = Math.sin(pos * Math.PI);
    const v =
      20 +
      (Math.sin(pos * Math.PI * 2 + seed * 0.1) * 14 +
        Math.sin(pos * Math.PI * 4 + seed * 0.05) * 8 +
        Math.sin(pos * Math.PI * 6 + seed * 0.03) * 5) *
        envelope *
        0.8;
    peaks.push(Math.max(4, Math.min(38, Math.round(v + 20))));
  }
  return peaks;
}

/**
 * Resolve the 48 peaks for a card waveform from the best available source.
 * Pure function: identical inputs produce identical outputs, so the call site
 * memoizes it with useMemo keyed on those inputs.
 */
function computePeaks(
  waveformData: number[] | null | undefined,
  decodedPeaks: number[] | null | undefined,
  soundId: string | undefined,
): number[] {
  const target = 48;
  const toCardScale = (values: number[], normalize: boolean) => {
    const out: number[] = [];
    for (let i = 0; i < target; i++) {
      const srcIdx = Math.floor((i / target) * values.length);
      const raw = values[srcIdx] ?? 20;
      // Decoded peaks come back 0-100; DB peaks are already in the card
      // pixel range. Both end up clamped to [2, 38].
      const scaled = normalize ? (raw / 100) * 38 : raw;
      out.push(Math.max(2, Math.min(38, Math.round(scaled))));
    }
    return out;
  };

  if (waveformData && waveformData.length > 0) return toCardScale(waveformData, false);
  if (decodedPeaks && decodedPeaks.length > 0) return toCardScale(decodedPeaks, true);
  return generatePeaks(soundId ?? "default");
}

// ── Props ──────────────────────────────────────────────────────────────────

export interface SoundCardProps {
  sound: Sound;
  className?: string;
  showCategory?: boolean;
  showNewBadge?: boolean;
  disableEntryAnimation?: boolean;
  /**
   * Defer the per-card audio subsystem (useAudioUrl URL resolution +
   * useAudioPeaks decode + audioPreloader) until the card actually scrolls
   * into view, even when `disableEntryAnimation` keeps the card visually
   * rendered from first paint. This keeps the card visible immediately (no
   * CLS) while moving 20+ cards' worth of audio hydration work off the
   * critical path — the dominant TBT cost on /sounds (TES-855).
   */
  deferAudioUntilVisible?: boolean;
  interactionSource?: "default" | "saved_page";
  keyboardShortcut?: number;
  /** Compatibility token for a caller-owned waveform role. Defaults to the one blue brand role. */
  accentToken?: string;
  /**
   * §04.1 density axis. Omit for d1 (default index, 68px/44px/27px) or d0
   * (mobile, handled automatically by the ≤639px breakpoint). Pass "d2" to
   * opt a row into the compact density (56px/40px/19px) for a secondary or
   * related list where more rows need to fit without becoming a grid.
   * Density only changes the resting row's scale — a playing row always
   * expands to the full plate regardless of density (Rule 2).
   */
  density?: "d2";
}

// ── Lazy loading hook ──────────────────────────────────────────────────────

const useLazyLoading = () => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Once the card has loaded there is nothing left to observe — skip
    // creating a new observer so each card observes at most once.
    if (hasLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          setHasLoaded(true);
        }
      },
      { rootMargin: "50px", threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasLoaded]);

  return { ref, isIntersecting, hasLoaded };
};

// ── Component ──────────────────────────────────────────────────────────────

const SoundCard: React.FC<SoundCardProps> = React.memo(
  ({
    sound,
    className = "",
    showCategory = true,
    showNewBadge = true,
    disableEntryAnimation = false,
    deferAudioUntilVisible = false,
    interactionSource = "default",
    keyboardShortcut,
    accentToken,
    density,
  }) => {
    const reactId = React.useId();
    const titleId = `sound-card-title-${reactId}`;
    const [mounted, setMounted] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    // §12.4 row→plate FLIP: prefers-reduced-motion skips the shared-element
    // transform entirely — the plate renders in its final position at 0ms
    // (§12.10's row for "Row → plate FLIP").
    const prefersReducedMotion = useReducedMotion();

    const { isFavorite, toggleFavorite, hasHydrated } = useFavoriteSounds();
    const isSaved = hasHydrated && isFavorite(sound.id);

    const base = soundUrl(sound);
    const detailHref = interactionSource === "saved_page" ? `${base}?from=saved` : base;

    const globalPlayer = useAudioPlayerContext();
    const isThisSoundActive = globalPlayer.currentSound?.id === sound?.id;
    const isThisSoundPlaying = isThisSoundActive && globalPlayer.isPlaying;
    const isThisSoundLoading = isThisSoundActive && globalPlayer.isLoading;

    const isNewSound = useIsRecent(showNewBadge ? sound?.created_at : null, 168);

    const needsTrimming = (sound?.duration || 0) > TESLA_REQUIREMENTS.MAX_DURATION;

    const { ref: lazyRef, isIntersecting, hasLoaded } = useLazyLoading();
    const shouldRenderFully = disableEntryAnimation || isIntersecting || hasLoaded;
    // Visual render can be immediate (no CLS) while audio resolution stays
    // visibility-gated. When deferAudioUntilVisible, the audio subsystem only
    // engages once the card intersects — even though disableEntryAnimation
    // keeps it painted. Playback itself is unaffected (the global audio
    // player context resolves its own URL on play).
    const shouldLoadAudio = deferAudioUntilVisible
      ? isIntersecting || hasLoaded
      : shouldRenderFully;

    // Observe the live card element so visibility is tracked even when the
    // card renders fully from first paint (skeleton branch is skipped).
    const setCardRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        cardRef.current = node;
        (lazyRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [lazyRef]
    );

    // When the sound came from an API that server-resolved a playable URL
    // (TES-831), pass it as the initial value so the card never resolves URLs
    // via storageApi in the browser.
    const { audioUrl } = useAudioUrl(
      shouldLoadAudio ? sound?.file_url || null : null,
      shouldLoadAudio ? sound?.audioUrl ?? null : null
    );

    useEffect(() => {
      if (audioUrl && sound?.id) {
        audioPreloader.preload(sound.id, audioUrl);
      }
    }, [audioUrl, sound?.id]);

    const mountedRef = useRef(false);
    useEffect(() => {
      const rafId = requestAnimationFrame(() => setMounted(true));
      mountedRef.current = true;
      return () => cancelAnimationFrame(rafId);
    }, []);

    // TES-829 + W3-1: one-shot engaged-preview / preview-completed flags,
    // derived from the global playback clock (only meaningful while this
    // sound is the active one).
    const { hasEngagedPreview, previewCompleted } = useEngagementTracking({
      isThisSoundPlaying,
      currentTime: globalPlayer.currentTime,
      duration: globalPlayer.duration,
      progress: globalPlayer.progress,
    });

    // Card download state + handler (tracking → increment → download order).
    const { isDownloading, downloadDone, handleCardDownload } = useCardDownload(
      sound,
      previewCompleted
    );

    const hasStoredPeaks = Boolean(sound?.waveform_data && sound.waveform_data.length > 0);
    // Decode real peaks from the audio file when the card is on-screen and the
    // DB has no precomputed waveform_data. useAudioPeaks caches per-URL and
    // dedupes in-flight fetches, so revisiting the same sound is free.
    const { peaks: decodedPeaks } = useAudioPeaks(
      audioUrl,
      48,
      shouldLoadAudio && !hasStoredPeaks && Boolean(audioUrl)
    );

    // Peaks for the card waveform — prefer real audio data, fall back to a
    // deterministic generator only while we wait for decode (so the card never
    // looks empty). All sources are normalized to the card's pixel range
    // [2, 38] to match the 36px-tall waveform component contract.
    // Memoized on the exact inputs computePeaks reads; React Compiler is not
    // enabled in this build, so without useMemo this recomputes every render.
    const peaks = useMemo(
      () => computePeaks(sound?.waveform_data, decodedPeaks, sound?.id),
      [sound?.waveform_data, decodedPeaks, sound?.id]
    );

    // ── Handlers ─────────────────────────────────────────────────────────

    const handlePlayPause = useCallback(
      (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (!mounted) return;
        // Loading counts as "engaged" so a second activation during the load
        // cancels it rather than re-issuing play(). The band's play control is
        // no longer `disabled` while loading (TES-1581), so this keeps it a
        // strict toggle — matching the detail-page player (TES-1579).
        if (isThisSoundPlaying || isThisSoundLoading) {
          globalPlayer.pause();
        } else {
          globalPlayer.play(sound);
          trackAudioPlay({ sound_id: sound.id, source: "sound_card", autoplay: false });
          trackPreviewPlay({
            sound_id: sound.id,
            sound_title: sound.title,
            category: sound.category,
            source: "sound_card",
          });
          if (interactionSource === "saved_page") trackSavedSoundsPlayClick(sound.id);
        }
      },
      [isThisSoundPlaying, isThisSoundLoading, globalPlayer, sound, mounted, interactionSource]
    );

    const handleFavoriteToggle = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const saved = toggleFavorite(sound.id);
        if (saved) {
          trackSavedSoundSaveClick({ soundId: sound.id, source: "sound_card" });
        } else {
          trackSavedSoundUnsaveClick({ soundId: sound.id, source: "sound_card" });
        }
      },
      [toggleFavorite, sound.id]
    );

    // ── Error / skeleton states ───────────────────────────────────────────

    if (!sound) {
      return (
        <div
          className="border-y p-4"
          style={{ borderColor: "var(--tls-border-subtle)", background: "var(--tls-surface)" }}
        >
          <div className="text-center text-sm" style={{ color: "var(--tls-fg-muted)" }}>
            Sound data not available
          </div>
        </div>
      );
    }

    if (!sound.id) {
      return (
        <div
          className="border-y p-4"
          style={{ borderColor: "var(--tls-border-subtle)", background: "var(--tls-surface)" }}
        >
          <div className="text-center text-sm" style={{ color: "var(--tls-fg-muted)" }}>
            Sound ID missing
          </div>
        </div>
      );
    }

    if (!shouldRenderFully) {
      return (
        <div ref={lazyRef}>
          <SoundCardSkeleton />
        </div>
      );
    }

    // ── Main render ───────────────────────────────────────────────────────

    return (
      <article
        ref={setCardRootRef}
        aria-labelledby={titleId}
        data-testid="sound-card"
        data-row-density={density}
        className={cn(
          "sc-marquee",
          isThisSoundActive && "is-active",
          isThisSoundPlaying && "is-playing",
          className
        )}
        style={{
          // The waveform carries the one shared blue accent. Callers can keep
          // an explicit legacy role during migration; all exposed roles resolve
          // to the same blue in the Plate & Hairline token layer.
          ["--sc-cat" as string]: accentToken ? `var(${accentToken})` : "var(--tls-c-brand)",
        }}
      >
        <WaveformBand
          peaks={peaks}
          progress={globalPlayer.progress}
          isPlaying={isThisSoundPlaying}
          isLoading={isThisSoundLoading}
          mounted={mounted}
          title={sound.title}
          onPlayPause={handlePlayPause}
          expanded={isThisSoundActive}
        />

        {/* ── Body ─────────────────────────────────────────────────────── */}
        {/* DS SoundRow anatomy, four grid items placed by grid-template-areas
            in globals.css: eyebrow (CATEGORY label), name, ONE right-aligned
            meta run (recency · duration · installs), controls. The resting
            state is a compact index row; the active root owns the larger
            plate, instead of making every result a tile. */}
        <div className="sc-content">
          <div className="sc-eyebrow">
            {showCategory && sound.category && (
              <Link
                href={categoryBrowseHref(sound.category)}
                onClick={e => e.stopPropagation()}
                // WCAG 2.5.8: min 44px hit area, with negative block margin
                // cancelling the growth so the eyebrow's compact footprint is
                // visually unchanged.
                style={{
                  textDecoration: "none",
                  minWidth: 0,
                  maxWidth: "100%",
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: "var(--touch-target-min, 44px)",
                  marginBlock: -15,
                }}
              >
                <span
                  className="sc-category"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    minWidth: 0,
                    fontFamily: "var(--tls-font-body)",
                    // §01.4 type-label: 11px is the floor for product UI.
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "var(--tls-fg-muted)",
                  }}
                >
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {sound.category}
                  </span>
                </span>
              </Link>
            )}
          </div>

          {/* The name: one line at rest on desktop, up to two on mobile, and
              it claims the plate when its own preview becomes active. */}
          <Link
            href={detailHref}
            onClick={e => {
              e.stopPropagation();
              trackSoundDetailClick({
                sound_id: sound.id,
                sound_title: sound.title,
                source: interactionSource,
              });
            }}
            // Desktop-pointer hit-area gap: the painted title measures
            // ~29.7px tall (one text line) against the row model's 44px
            // floor. Same pattern as the nav's route pills — a padded
            // ::before target, not bigger type (PRODUCT.md: "the 44px
            // target is a padded wrapper, not a taller button"). Gated to
            // `sm:` (640px) and up: below that, `.sc-title` wraps to two
            // lines and the row's own min-height already clears 44px, so
            // expanding further would eat into the ~16px margin above/below
            // and risk overlapping the neighbouring row's target. At
            // desktop widths the row's min-height (68px) leaves ~19px of
            // margin per side around the 29.7px link box, so an 8px
            // inset-y expansion (→ ~45.7px) has ~11px of clearance left
            // before it would reach the hairline border, let alone the
            // next row's own target.
            className="sc-title-link relative before:absolute before:inset-x-0 before:content-[''] sm:before:-inset-y-2"
            style={{ textDecoration: "none", display: "block", minWidth: 0 }}
          >
            <motion.h3
              id={titleId}
              className="sc-title"
              // §12.4 — the row IS the plate's expanded state (Rule 2), so
              // this is the same DOM node changing size via the .is-active
              // class, not two different nodes swapping. `layout` alone
              // (no layoutId — there is nothing else to match against)
              // gives Framer the before/after rects to FLIP between.
              // Gated to the active row only: an inactive row's title
              // never opts into layout tracking, so Framer never has to
              // measure or project a rect for it — the 1,400-row cost the
              // spec's flipRules warn about is paid by at most one row at
              // a time.
              layout={isThisSoundActive ? "position" : false}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { duration: 0.22, ease: [0.2, 0, 0, 1] }
              }
              style={{
                // Size, weight and tracking live in .sc-title, NOT here.
                // Rule 2 steps them up when the row is playing, and an inline
                // style outranks any class rule, so setting them here made
                // the expansion silently do nothing but change case.
                margin: 0,
                fontFamily: "var(--tls-font-display)",
                color: "var(--tls-fg-strong)",
              }}
            >
              {cleanSoundTitle(sound.title)}
            </motion.h3>
          </Link>

          <CardActions
            title={sound.title}
            downloads={sound.downloads ?? 0}
            duration={sound.duration}
            needsTrimming={needsTrimming}
            isNew={isNewSound}
            isPremium={Boolean(sound.is_premium)}
            isPlaying={isThisSoundPlaying}
            hasEngagedPreview={hasEngagedPreview}
            previewCompleted={previewCompleted}
            isDownloading={isDownloading}
            downloadDone={downloadDone}
            isSaved={isSaved}
            keepSaveOnMobile={interactionSource === "saved_page"}
            onDownload={handleCardDownload}
            onFavoriteToggle={handleFavoriteToggle}
          />
        </div>
      </article>
    );
  }
);

SoundCard.displayName = "SoundCard";

export default SoundCard;
